import { Component, OnInit, OnDestroy } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';

import { Subscription } from "rxjs";
import { tap } from "rxjs/operators";
import { GridOptions, ColDef, GridApi } from "ag-grid-community";

import { ActionCellRendererComponent } from '../../../common/components/ag-grid/action-cell-renderer.component';
import { dateComparator } from '../../../common/components/data-table/comparators/data-table.comparators';

import { DateService } from "../../../common/services/date.service";
import { GridFormatterService } from "../../../common/services/grid-formatter.service";
import { PaymentService } from "../../services/payment.service";
import { SubHeaderService } from '../../../core/services/sub-header.service';
import { PaymentSearchCriteria, PaymentStatus, ApplicationPayment } from "../../models/paymentItem.model";
import { SubHeader } from '../../../core/models/sub-header.model';

@Component({
    selector: 'payment-open-items-report',
    templateUrl: './payment-open-items-report.component.html',
    styleUrls: ['./payment-open-items-report.component.scss'],
    standalone: false
})

export class PaymentOpenItemsReportComponent implements OnInit, OnDestroy {

    payments: ApplicationPayment[];
	gridOptions: GridOptions;
	gridApi?: GridApi;

    currentDate: Date = new Date();
	
    datePast?: Date | null;
    dateFuture?: Date | null;

    toDate?: Date | null;
    fromDate?: Date | null;

    subHeader: SubHeader = new SubHeader({
        title: "Listing of Open Items",
        buttons: []
    });

    subscription: Subscription = new Subscription();

    constructor(
        private datePipe: DatePipe,
        private readonly dateService: DateService,
        private readonly formatService: GridFormatterService,
        private readonly paymentService: PaymentService,
        public router: Router,
        public subHeaderService: SubHeaderService
	) { }

    ngOnInit() {
        this.subHeaderService.updateSubHeader(this.subHeader);

        let coldefs = this.createColumnDefinitions();
        this.gridOptions = this.formatService.BuildDefaultGridOptionsWithColumns(coldefs, false, true, true, true);
        this.gridOptions.overlayLoadingTemplate = '<span class="ag-overlay-loading-center">Please wait ...</span>';
		this.gridOptions.onGridReady = (params) => { this.gridApi = params.api; };
        this.generateReport();
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    fromDateChange(evt: Date | null): void {
        this.fromDate = evt;
        if (this.fromDate != null) {
            this.datePast = this.fromDate;
            this.datePast.setDate(this.fromDate.getDate() + 1);
        } else {
            this.datePast = null;
        }
    }

    toDateChange(evt: Date | null): void {
        this.toDate = evt;
        if (this.toDate != null) {
            this.dateFuture = this.toDate;
            this.dateFuture.setDate(this.toDate.getDate() - 1);
        } else {
            this.dateFuture = null;
        }
    }

    generateReport() {
        if (this.gridApi) {
            this.gridApi.setGridOption("loading", true);
        }

        const criteria = new PaymentSearchCriteria("", "", [PaymentStatus.Pending, PaymentStatus.Partial, PaymentStatus.Overdue]);
        criteria.fromSubmissionDate = this.fromDate;
        criteria.toSubmissionDate = this.toDate;

        this.subscription = this.paymentService.search(criteria).pipe(
            tap((data: ApplicationPayment[]) => {
                this.payments = data; 
            })).subscribe({
                next: (success) => {
                    const params = {
                        force: true
                    };
                    if (this.gridApi != null) {
                        this.gridApi.setGridOption("rowData", this.payments);
                        this.gridApi.setGridOption("loading", false);
                        this.gridApi.sizeColumnsToFit();
                    }
                    
                }, 
                error: (fail) => {
                    console.error(fail);
                    if (this.gridApi != null) {
                        this.gridApi.setGridOption("loading", false);
                    }
                }
            });
    }

    downloadReport() {
        const params = {
            skipHeader: false,
            allColumns: true,
            fileName: this.formatFileName(),
            sheetName: "Open Items",
            exportMode: "xlsx"
        }
        this.gridApi.exportDataAsCsv(params);
    }

    formatFileName(): string {
        let fileName;
        if (!this.fromDate || !this.toDate) {
            const today = this.datePipe.transform(new Date(),"yyyy-MM-dd");
            fileName = `OpenItems_${today}`;
        } else {
            const fromDate = this.datePipe.transform(this.fromDate, "yyyy-MM-dd");
            const toDate = this.datePipe.transform(this.toDate, "yyyy-MM-dd");
            fileName = `OpenItems_${fromDate}_${toDate}`;
        }
        return fileName;
    }

    navigateToOrgDetail() {
        return (obj) => {
            const id = obj.data.organizationId;
            this.router.navigate(['/common/organization', id]);
        }
    }

    public createColumnDefinitions() {
        return [
            {
                headerName: "Company Name",
                headerTooltip: "Company Name",
                field: "organization",
                tooltipField: 'organization',
                cellRenderer: ActionCellRendererComponent,
                cellRendererParams: {
                    textField: 'organization',
                    action: this.navigateToOrgDetail()
                },
                filterParams: this.formatService.defaultTextFilterParams,
                filter: "agTextColumnFilter",
                minWidth: 250
            },
            {
                headerName: "Submitted Date",
                headerTooltip: "Submitted Date",
                field: "submissionDate",
                tooltipValueGetter: this.formatService.formatDate("submissionDate"),
                valueGetter: this.formatService.formatDate("submissionDate"),
                comparator: dateComparator,
                filterParams: this.formatService.defaultDateFilterParams,
                filter: "agDateColumnFilter"
            },
            {
                headerName: "Fee Amount",
                headerTooltip: "Fee Amount",
                field: "amountDue",
                tooltipValueGetter: this.formatService.formatCurrency("amountDue", "USD", "symbol", "1.2-2"),
                valueFormatter: this.formatService.formatCurrency("amountDue", "USD", "symbol", "1.2-2"),
                filterParams: this.formatService.defaultNumberFilterParams,
                filter: "agNumberColumnFilter"
            },
            {
                headerName: "Application #",
                headerTooltip: "Application #",
                field: "applicationNumber",
                tooltipField: 'applicationNumber',
                filterParams: this.formatService.defaultTextFilterParams,
                filter: "agTextColumnFilter"
            },
            {
                headerName: "Item Type Open",
                headerTooltip: "Item Type Open",
                field: "applicationType",
                tooltipField: 'applicationType',
                filterParams: this.formatService.defaultTextFilterParams,
                filter: "agTextColumnFilter",
                minWidth: 250
            }

        ] as ColDef[];
    }

    onGridPageChanged(event: any) {
        if (this.gridApi != null) this.gridApi.sizeColumnsToFit();
    }
}
